import { RootState } from "shared/config/store"
import { createSelector } from '@reduxjs/toolkit'
import { assetRootBoardIdSelector, rootBoardIdSelector } from "features/user/model/selectors/userSelectors"

import { boardsSelectors } from "../slice/boardsSlice"
import { selectSelectedSort, selectSortOrder } from '../../../common/model/selectors/appSelectors'
import { sortComparer } from '../../../common/utils/itemsSort'
import type { SystemField } from '../types/boards'

export const selectedBoardsIdsSelector = (state: RootState) => state.boards.selectedItemsIds

export const allBoardsSelector = (state: RootState) => boardsSelectors.selectAll(state.boards)

export const currentBoardSelector = (state: RootState) => state.boards.currentBoard

export const currentBoardIdSelector = (state: RootState) => state.boards.currentBoardId

export const pageStatusSelector = (state: RootState) => state.boards.status

export const selectedParentIdSelector = (state: RootState) => state.boards.selectedParentId

export const getSystemFieldsOptions = (state: RootState) => state.boards.systemFieldsOptions

export const getBoardFieldsValues = (state: RootState) => state.boards.boardFieldsValues

export const getSystemFieldsValues = (state: RootState) => state.boards.systemFieldsValues

export const getSystemFields = (state: RootState) => state.boards.systemFields

export const getBoardFields = (state: RootState) => state.boards.boardFields

export const getFilteredBoardsSelector = createSelector(
  allBoardsSelector,
  rootBoardIdSelector,
  assetRootBoardIdSelector,
  (boards, rootBoardId, assetsRootBoardId) => {
    if(!assetsRootBoardId) return []
    return boards.filter(board => board.id !== assetsRootBoardId && board.id !== rootBoardId)
  }
)

/**
 * Memoized selector for system statuses with proper fallback
 */
export const getSystemStatuses = createSelector(
  getSystemFieldsValues,
  (data) => {
    if (!data?.statusOptions?.length) return []
    return data.statusOptions
  }
)

/**
 * Memoized selector for system tags with proper fallback
 */
export const getSystemTags = createSelector(
  getSystemFieldsValues,
  (data) => {
    if (!data?.tags?.length) return []
    return data.tags
  }
)

export const getBoardStatus = createSelector(
  getBoardFieldsValues,
  (data) => {
    if(!data.status) return null
    return data.status
  }
)

export const getBoardTags = createSelector(
  getBoardFieldsValues,
  (data) => {
    if(!data.tags) return []
    return data.tags
  }
)

export const getBoardDescription = createSelector(
  getBoardFieldsValues,
  (data) => {
    if(!data || data.description === null || data.description === undefined) return ''
    return data.description
  }
)

/**
 * Helper function to get field ID by title with proper typing
 */
const getFieldByTitle = (title: string, data: SystemField[] | null): string | null => {
  if (!data || !Array.isArray(data)) return null
  const fieldData = data.find((field) => field?.title === title)
  return fieldData?.id || null
}

export const getSystemTagsId = createSelector(
  getSystemFields,
  (data) => {
    return getFieldByTitle('tags', data)
  }
)

export const getStatusFieldId = createSelector(
  getSystemFields,
  (data) => {
    return getFieldByTitle('status', data)
  }
)

export const getDescriptionFieldId = createSelector(
  getSystemFields,
  (data) => {
    return getFieldByTitle('description', data)
  }
)

/**
 * Complete board data selector that combines all field values and IDs
 * Replaces multiple individual selectors for better performance
 */
export const getBoardCompleteData = createSelector(
  [getBoardFieldsValues, getSystemFields],
  (fieldValues, systemFields) => {
    const getFieldId = (title: string): string | null =>
      systemFields?.find(field => field?.title === title)?.id || null

    return {
      // Field values with proper defaults
      status: fieldValues?.status ?? null,
      tags: fieldValues?.tags ?? [],
      description: fieldValues?.description ?? '',

      // Field IDs for API operations
      fieldIds: {
        status: getFieldId('status'),
        tags: getFieldId('tags'),
        description: getFieldId('description')
      },

      // Helper methods for checking field availability
      hasStatusField: () => Boolean(getFieldId('status')),
      hasTagsField: () => Boolean(getFieldId('tags')),
      hasDescriptionField: () => Boolean(getFieldId('description')),

      // Validation helpers
      isValidStatus: (statusId: string) => Boolean(statusId && getFieldId('status')),
      isValidTagsList: (tags: string[]) => Array.isArray(tags) && Boolean(getFieldId('tags')),
      isValidDescription: (description: string) => typeof description === 'string' && Boolean(getFieldId('description'))
    }
  }
)

export const selectSortedBoards = createSelector(
  [getFilteredBoardsSelector, selectSelectedSort, selectSortOrder],
  (bards, sortKey, sortDirection) => {
    if (!bards.length) return bards
    return [...bards].sort(sortComparer.bind(null, { sortDirection, sortKey } ))
  }
)

